import { OrganizationCard } from "@/components/cards";
import { useCrewHandler } from "@/sites/plan/plan-kanban/use-crew-handler";
import { Button, CollapseButton } from "@msuite/ui/button";
import { Draggable } from "@msuite/ui/dnd";
import type { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { Tag } from "@msuite/ui/tag";
import { cn } from "@msuite/ui/utils";
import { queries } from "@msuite/zero";
import { useZeroQuery } from "@msuite/zero/react";
import React from "react";
import { useDailyContext } from "../daily/context";
import { useCrewContext } from "./context";
import { CrewMenu } from "./crew-menu";

/** Props Interface */
interface CrewHeaderProps {
	controller: ReturnType<typeof useDisclosure>;
}

export function CrewHeader({ controller }: CrewHeaderProps) {
	/** Context */
	const { isInteractionBlocked } = useDailyContext();
	const { crew, crewId, planId } = useCrewContext();
	const crewHandler = useCrewHandler({ crewId, planId });
	const [vehicleAssignment] = useZeroQuery(queries.vehicleAssignments.byCrewId({ crewId }));

	/** Hooks */
	const asyncModal = useAsyncModal();
	const { t } = useTranslation();

	/** Handlers */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		crewHandler.onDeleteCrew();
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="top-0 z-10 sticky">
				<div
					className={cn(
						"top-0 z-10 sticky flex flex-row items-center gap-2 px-1 py-0 card",
						"h-8.5",
						"hover:[&_.crew-delete-button]:opacity-100!",
					)}
					onDoubleClick={controller?.onToggle}
				>
					<CollapseButton
						{...controller}
						size="xxs"
					/>
					<OrganizationCard
						orgId={crew.orgId}
						variant="plain"
						fontWeight="medium"
						withAvatar={false}
						suffix={crew.ranking}
					/>
					<div className="flex-1" />
					{vehicleAssignment && (
						// Dragging the tag out of the column is how a vehicle is taken off a crew, so
						// it is the same write as the delete button below and carries the same
						// condition. Left open it is the one control on the board that lets a caller
						// without `update Plan` change a day.
						<Draggable
							type="vehicle-assignment"
							item={vehicleAssignment}
							isDisabled={isInteractionBlocked}
						>
							<Tag color="accent">{vehicleAssignment.vehicle?.licensePlate}</Tag>
						</Draggable>
					)}
					{!isInteractionBlocked && (
						<Button
							className="opacity-0! crew-delete-button"
							icon={<Icon name="Trash2" />}
							variant="danger-ghost"
							onClick={handleOnDelete}
							isLoading={asyncModal.isPending}
							size="xxs"
						/>
					)}
					<CrewMenu onDelete={handleOnDelete} />
				</div>
			</div>
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deletion")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
